import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  MdCheckCircle,
  MdHourglassTop,
  MdInfoOutline,
  MdVerifiedUser,
} from 'react-icons/md';
import {
  AppSelect,
  AppTextarea,
  OutlineButton,
  PrimaryButton,
  StatusBadge,
} from '../../../../components/ui';
import { ASSET_KYC_OPTIONS } from '../../../../data/master/equipmentAssets';
import { toApiStatus } from '../../../../models/_helpers';
import { updateEquipmentKyc } from '../../../../repositories/equipment.repository';
import { updateUserEquipmentKyc } from '../../../../repositories/userEquipment.repository';

function badgeStatus(status) {
  if (status === 'Verified') return 'verified';
  if (status === 'Rejected') return 'error';
  return 'pending';
}

function StatusNote({ status }) {
  if (status === 'Verified') {
    return (
      <Box
        p={3.5}
        bg="green.50"
        borderRadius="md"
        borderWidth="1px"
        borderColor="green.200"
      >
        <Flex align="flex-start" gap={2.5}>
          <Icon as={MdCheckCircle} boxSize={5} color="status.success" mt="1px" flexShrink={0} />
          <Box>
            <Text fontSize="13px" fontWeight="700" color="text.primary" mb={1}>
              Ready for recommendations
            </Text>
            <Text fontSize="13px" color="text.secondary" lineHeight="1.45">
              Appears in Find & recommend. Members can still update compliance
              dates (RC / PUC / insurance / tax / fitness) if needed —
              registration, chassis, and engine stay locked. Team can edit all
              fields from Edit details.
            </Text>
          </Box>
        </Flex>
      </Box>
    );
  }

  if (status === 'Rejected') {
    return null;
  }

  return (
    <Box
      p={3.5}
      bg="bg.amberTint"
      borderRadius="md"
      borderWidth="1px"
      borderColor="utkarsh.amber"
    >
      <Flex align="flex-start" gap={2.5}>
        <Icon as={MdHourglassTop} boxSize={5} color="status.warning" mt="1px" flexShrink={0} />
        <Box>
          <Text fontSize="13px" fontWeight="700" color="text.primary" mb={1}>
            Awaiting review
          </Text>
          <Text fontSize="13px" color="text.secondary" lineHeight="1.45">
            Keeps this equipment pending. The owner can edit and resubmit until
            you verify or reject it.
          </Text>
        </Box>
      </Flex>
    </Box>
  );
}

export function EquipmentKycStatusModal({
  isOpen,
  onClose,
  asset,
  onSaved,
  useMemberEquipment = false,
}) {
  const toast = useToast();
  const [status, setStatus] = useState('Pending');
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !asset) return;
    setStatus(asset.status || 'Pending');
    setReason(asset.rejectionReason || '');
    setReasonError('');
  }, [isOpen, asset]);

  if (!asset) return null;

  const handleSave = async (e) => {
    e.preventDefault();
    if (status === 'Rejected') {
      const trimmed = reason.trim();
      if (trimmed.length < 5) {
        setReasonError('Enter a clear rejection reason (min 5 characters)');
        return;
      }
      if (trimmed.length > 500) {
        setReasonError('Max 500 characters');
        return;
      }
    }
    if (asset.id == null) {
      toast({ title: 'Missing equipment id', status: 'error', position: 'top-right' });
      return;
    }

    setSaving(true);
    try {
      if (useMemberEquipment) {
        await updateUserEquipmentKyc(asset.id, {
          kycStatus: status,
          rejectionReason: status === 'Rejected' ? reason.trim() : null,
        });
      } else {
        await updateEquipmentKyc(asset.id, { kycStatus: toApiStatus(status) });
      }
      toast({
        title: 'KYC status updated',
        description: `${asset.assetId} · ${status}`,
        status: 'success',
        position: 'top-right',
      });
      onSaved?.();
      onClose();
    } catch (err) {
      toast({
        title: err?.message || 'Failed to update KYC',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  const title =
    asset.equipmentDescription || asset.equipmentType || asset.assetId;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        as="form"
        onSubmit={handleSave}
        bg="bg.surface"
        borderRadius="md"
        mx={4}
        my={4}
        maxH="90vh"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <ModalHeader flexShrink={0} pb={1}>
          Update status
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody flex="1" overflowY="auto" minH={0} pt={1} pb={4}>
          <VStack align="stretch" spacing={4}>
            <Flex align="center" gap={3}>
              <Flex
                w="40px"
                h="40px"
                align="center"
                justify="center"
                borderRadius="md"
                bg="bg.amberTint"
                flexShrink={0}
              >
                <Icon as={MdVerifiedUser} boxSize={5} color="utkarsh.ink" />
              </Flex>
              <Box minW={0} flex="1">
                <Text fontWeight="700" fontSize="15px" noOfLines={1}>
                  {title}
                </Text>
                <Flex align="center" gap={2} mt={0.5} wrap="wrap">
                  <Text fontSize="12px" fontFamily="mono" color="text.secondary">
                    {asset.assetId}
                  </Text>
                  <StatusBadge status={badgeStatus(asset.status || 'Pending')}>
                    {asset.status || 'Pending'}
                  </StatusBadge>
                </Flex>
              </Box>
            </Flex>

            <AppSelect
              label="New status"
              isRequired
              value={status}
              onChange={(v) => {
                setStatus(v);
                setReasonError('');
                if (v !== 'Rejected') setReason('');
              }}
              options={ASSET_KYC_OPTIONS}
            />

            <StatusNote status={status} />

            {status === 'Rejected' ? (
              <Box
                p={3.5}
                bg="bg.errorTint"
                borderRadius="md"
                borderWidth="1px"
                borderColor="status.error"
              >
                <Flex align="flex-start" gap={2.5} mb={3}>
                  <Icon as={MdInfoOutline} boxSize={5} color="status.error" mt="1px" flexShrink={0} />
                  <Box>
                    <Heading size="xs" mb={1} color="status.error">
                      Rejection reason
                    </Heading>
                    <Text fontSize="13px" color="text.secondary" lineHeight="1.45">
                      The owner will see this and can edit & resubmit the
                      equipment.
                    </Text>
                  </Box>
                </Flex>
                <AppTextarea
                  label="Reason"
                  isRequired
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                    setReasonError('');
                  }}
                  placeholder="e.g. Missing fitness certificate / RC mismatch"
                  rows={3}
                  maxLength={500}
                  isInvalid={!!reasonError}
                  errorMessage={reasonError}
                  helperText={`${reason.trim().length}/500`}
                />
              </Box>
            ) : null}
          </VStack>
        </ModalBody>
        <ModalFooter gap={2} borderTopWidth="1px" borderColor="border.subtle">
          <OutlineButton type="button" onClick={onClose} isDisabled={saving}>
            Cancel
          </OutlineButton>
          <PrimaryButton type="submit" isLoading={saving}>
            Save status
          </PrimaryButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
